export type TemplateId =
  | "stat"
  | "quote"
  | "checklist"
  | "announcement"
  | "question"
  | "cta"
  | "proof"
  | "whywewin"
  | "support"
  | "marketpulse"
  | "recruit"
  | "casestudy";

export type TemplateTag = "stat" | "educational" | "proof" | "promo" | "recruiting";

export const TEMPLATE_TAGS: { id: TemplateTag; label: string }[] = [
  { id: "stat", label: "Stat" },
  { id: "educational", label: "Educational" },
  { id: "proof", label: "Trust & Proof" },
  { id: "promo", label: "Promo" },
  { id: "recruiting", label: "Recruiting" },
];


export interface SocialSize {
  id: string;
  label: string;
  width: number;
  height: number;
}

export const SOCIAL_SIZES: SocialSize[] = [
  { id: "square", label: "Square 1:1 (Instagram / Facebook)", width: 1080, height: 1080 },
  { id: "story", label: "Story 9:16 (Reels / Stories)", width: 1080, height: 1920 },
  { id: "linkedin", label: "Link post 1.91:1 (LinkedIn / X)", width: 1200, height: 628 },
];

export interface Palette {
  id: string;
  label: string;
  bg: string;
  bgAlt: string;
  accent: string;
  text: string;
  muted: string;
}

export const PALETTES: Palette[] = [
  {
    id: "midnight",
    label: "Midnight Gold",
    bg: "#0A1628",
    bgAlt: "#0F1E36",
    accent: "#FBBF24",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.62)",
  },
  {
    id: "cream",
    label: "Cream & Ink",
    bg: "#F4EDE3",
    bgAlt: "#EBE1D3",
    accent: "#B45309",
    text: "#1A1612",
    muted: "rgba(26,22,18,0.62)",
  },
  {
    id: "emerald",
    label: "Deep Emerald",
    bg: "#062A22",
    bgAlt: "#0A3A2F",
    accent: "#34D399",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
  },
  {
    id: "slate",
    label: "Slate & Sky",
    bg: "#0F172A",
    bgAlt: "#17233D",
    accent: "#60A5FA",
    text: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
  },
];

/** Build a palette from user hex values. */
export function customPalette(
  bg: string,
  bgAlt: string,
  accent: string,
  text: string
): Palette {
  return {
    id: "custom",
    label: "Custom",
    bg,
    bgAlt,
    accent,
    text,
    muted: hexToRgba(text, 0.62),
  };
}

export function isValidHex(v: string) {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v.trim());
}

export function hexToRgba(hex: string, alpha: number) {
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return `rgba(255,255,255,${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/* ---------------- contrast helpers ---------------- */

function hexToRgb(hex: string): [number, number, number] {
  let h = (hex || "").trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number) {
  const c = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** Perceived luminance 0 (black) → 1 (white). */
export function luminance(hex: string) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Average luminance of an image, sampled cheaply on a tiny canvas. */
export function imageLuminance(img: HTMLImageElement): number | null {
  try {
    const c = document.createElement("canvas");
    c.width = 16;
    c.height = 16;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, 16, 16);
    const data = ctx.getImageData(0, 0, 16, 16).data;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
      count++;
    }
    return count ? sum / count : null;
  } catch {
    return null;
  }
}

function shade(hex: string, amount: number) {
  const [r, g, b] = hexToRgb(hex);
  if (amount >= 0) {
    return rgbToHex(
      r + (255 - r) * amount,
      g + (255 - g) * amount,
      b + (255 - b) * amount
    );
  }
  const k = 1 + amount;
  return rgbToHex(r * k, g * k, b * k);
}

/**
 * Returns text/muted/accent colors that stay legible on the given background
 * luminance, complementing the chosen palette instead of fighting it.
 */
export function contrastColors(bgLum: number, accent: string) {
  const light = bgLum < 0.45;
  const text = light ? "#FFFFFF" : "#14110D";
  const muted = hexToRgba(text, light ? 0.68 : 0.62);
  let safeAccent = accent;
  const aLum = luminance(accent);
  if (light && aLum < 0.32) safeAccent = shade(accent, 0.45);
  if (!light && aLum > 0.5) safeAccent = shade(accent, -0.35);
  return { text, muted, accent: safeAccent };
}


/* ---------------- industry sources ---------------- */

export interface IndustrySource {
  id: string;
  topic: string;
  insight: string;
  /** Short paraphrase usable as the graphic's main message. */
  headline: string;
  /** Prompt shown to the user for tying their own story to this reference. */
  notePrompt: string;
  citation: string;
  outlet: string;
  url: string;
}

/**
 * Curated, real, publicly reachable industry references.
 * The picker rotates through these daily (see getDailySources).
 */
export const INDUSTRY_SOURCES: IndustrySource[] = [
  {
    id: "cms-marketplace",
    notePrompt: "How your team helps clients use their Marketplace coverage better.",
    headline: "More people on Marketplace plans means more coverage gaps to fill.",
    topic: "Marketplace enrollment",
    insight:
      "Marketplace enrollment keeps expanding the pool of clients who need year-round ancillary coverage.",
    citation: "CMS Marketplace Enrollment Data",
    outlet: "CMS",
    url: "https://www.cms.gov/marketplace/enrollment-data",
  },
  {
    id: "kff-health-coverage",
    notePrompt: "How you keep clients ahead of plan-year changes.",
    headline: "Coverage and premiums shift every plan year — quote from current data.",
    topic: "Coverage landscape",
    insight:
      "Coverage mix and premium trends shift every plan year — check the current state data before you quote.",
    citation: "KFF State Health Facts",
    outlet: "KFF",
    url: "https://www.kff.org/state-health-policy-data/",
  },
  {
    id: "limra-life-gap",
    notePrompt: "How you close the life coverage gap for your clients.",
    headline: "Most households know they are underinsured on life coverage.",
    topic: "Life insurance gap",
    insight:
      "A large share of households say they need more life coverage than they currently own.",
    citation: "LIMRA Insurance Barometer Study",
    outlet: "LIMRA",
    url: "https://www.limra.com/en/newsroom/industry-trends/",
  },
  {
    id: "healthcare-gov-sep",
    notePrompt: "How you catch clients during life events.",
    headline: "Life events open coverage windows all year long.",
    topic: "Special Enrollment Periods",
    insight:
      "Qualifying life events open coverage windows all year — that is when cross-sell conversations land.",
    citation: "HealthCare.gov Special Enrollment",
    outlet: "HealthCare.gov",
    url: "https://www.healthcare.gov/coverage-outside-open-enrollment/special-enrollment-period/",
  },
  {
    id: "eba-worksite",
    notePrompt: "How you build worksite bundles for your groups.",
    headline: "Worksite benefits are the fastest path to a second policy.",
    topic: "Worksite & voluntary benefits",
    insight:
      "Voluntary and worksite products are the fastest path to a second policy per household.",
    citation: "Employee Benefit News",
    outlet: "EBN",
    url: "https://www.benefitnews.com/voluntary-benefits",
  },
  {
    id: "naic-consumer",
    notePrompt: "How you keep your brokers compliant.",
    headline: "State rules decide what you can market — check before you publish.",
    topic: "Regulatory & consumer guidance",
    insight:
      "State rules shape what you can market and how — verify before you publish a claim.",
    citation: "NAIC Consumer Insurance Resources",
    outlet: "NAIC",
    url: "https://content.naic.org/consumer.htm",
  },
  {
    id: "irs-aca-2026",
    notePrompt: "How you guide clients through affordability changes.",
    headline: "Affordability thresholds reset each year and change who qualifies.",
    topic: "ACA affordability rules",
    insight:
      "Affordability thresholds reset each plan year and change who qualifies for subsidies.",
    citation: "IRS Affordable Care Act Guidance",
    outlet: "IRS",
    url: "https://www.irs.gov/affordable-care-act",
  },
  {
    id: "ahip-coverage",
    notePrompt: "How your business closes those gaps.",
    headline: "Industry research explains why coverage gaps persist.",
    topic: "Industry advocacy & data",
    insight:
      "Industry-wide research helps frame why coverage gaps persist in your market.",
    citation: "AHIP Research",
    outlet: "AHIP",
    url: "https://www.ahip.org/resources",
  },
  {
    id: "census-uninsured",
    notePrompt: "How you reach the uninsured in your market.",
    headline: "Local uninsured rates show where gap coverage sells best.",
    topic: "Uninsured & underinsured",
    insight:
      "Local uninsured rates tell you exactly where the gap-coverage conversation is strongest.",
    citation: "U.S. Census Health Insurance Data",
    outlet: "U.S. Census Bureau",
    url: "https://www.census.gov/topics/health/health-insurance.html",
  },
];

/** Stable UTC day key, e.g. 2026-08-21 */
export function todayKey(d: Date = new Date()) {
  return d.toISOString().slice(0, 10);
}

/**
 * Deterministic daily rotation: everyone sees the same set on a given day,
 * and the featured sources change automatically at UTC midnight.
 */
export function getDailySources(count = 6, d: Date = new Date()): IndustrySource[] {
  const key = todayKey(d);
  let seed = 0;
  for (let i = 0; i < key.length; i++) seed = (seed * 31 + key.charCodeAt(i)) % 100000;
  const list = [...INDUSTRY_SOURCES];
  // seeded rotation + shuffle
  const out: IndustrySource[] = [];
  let s = seed || 1;
  while (list.length && out.length < count) {
    s = (s * 1103515245 + 12345) % 2147483648;
    out.push(list.splice(s % list.length, 1)[0]);
  }
  return out;
}


export interface TemplateContent {
  eyebrow: string;
  headline: string;
  body: string;
  stat: string;
  statLabel: string;
  bullets: string[];
  footer: string;
  note: string;
  source: string;
  sourceUrl: string;
}

export interface TemplateDef {
  id: TemplateId;
  name: string;
  description: string;
  tags: TemplateTag[];
  fields: (keyof TemplateContent)[];
  defaults: Partial<TemplateContent>;
}

export const TEMPLATES: TemplateDef[] = [

  {
    id: "stat",
    name: "Big Stat",
    description: "One number that stops the scroll.",
    tags: ["stat", "educational"],
    fields: ["eyebrow", "stat", "statLabel", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Ancillary Insight",
      stat: "__%",
      statLabel: "add your own verified figure and cite its source",
      body: "Replace this placeholder with a stat you can verify from your book or a cited reference.",
      footer: "chirpwell.com",

    },
  },
  {
    id: "quote",
    name: "Pull Quote",
    description: "Editorial quote card with oversized mark.",
    tags: ["educational"],
    fields: ["eyebrow", "headline", "note", "source", "footer"],
    defaults: {
      eyebrow: "Broker Notes",
      headline: "You do not need more clients. You need more product per client.",
      footer: "Chirpwell",
    },
  },
  {
    id: "checklist",
    name: "Checklist",
    description: "Three to five scannable takeaways.",
    tags: ["educational"],
    fields: ["eyebrow", "headline", "bullets", "note", "source", "footer"],
    defaults: {
      eyebrow: "Cross-Sell Checklist",
      headline: "Before your next renewal call",
      bullets: [
        "Know the client's current gaps",
        "Lead with one product, not five",
        "Quote monthly, not annual",
        "Confirm the effective date",
      ],
      footer: "chirpwell.com",
    },
  },
  {
    id: "announcement",
    name: "Announcement",
    description: "Bold headline over a split field.",
    tags: ["promo"],
    fields: ["eyebrow", "headline", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Now Live",
      headline: "Cross-Sell Revenue Calculator",
      body: "Model first-year and renewal revenue in under a minute.",
      footer: "chirpwell.com",
    },
  },
  {
    id: "question",
    name: "Question Hook",
    description: "Engagement prompt built for comments.",
    tags: ["educational"],
    fields: ["eyebrow", "headline", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Question for Brokers",
      headline: "What is your ancillary attach rate?",
      body: "Drop a number in the comments. Most guesses are 20 points too high.",
      footer: "Chirpwell",
    },
  },
  {
    id: "cta",
    name: "Call to Action",
    description: "Clean closer with a boxed action.",
    tags: ["promo"],
    fields: ["eyebrow", "headline", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Free Tool",
      headline: "See the revenue hiding in your book",
      body: "Run your numbers with the Chirpwell cross-sell calculator.",
      footer: "chirpwell.com",
    },
  },
  {
    id: "proof",
    name: "Proof Point",
    description: "Industry stat + why your shop wins.",
    tags: ["proof", "stat"],
    fields: ["eyebrow", "stat", "statLabel", "note", "source", "footer"],
    defaults: {
      eyebrow: "Industry Proof Point",
      stat: "__%",
      statLabel: "add a verifiable figure from a cited source or your own book",
      note: "We build the cross-sell plan with you, so your book gets there instead of guessing at it.",
      footer: "chirpwell.com",

    },
  },
  {
    id: "whywewin",
    name: "Why We Win",
    description: "Your differentiator, stated plainly.",
    tags: ["recruiting", "proof"],
    fields: ["eyebrow", "headline", "note", "source", "footer"],
    defaults: {
      eyebrow: "Why Brokers Partner With Us",
      headline: "Year-round revenue, not a 60-day season",
      note: "Our brokers add multi-line revenue between AEP and OEP with tools, training and real support.",
      footer: "chirpwell.com",
    },
  },
  {
    id: "support",
    name: "Broker Support",
    description: "What brokers get, in a scannable list.",
    tags: ["recruiting"],
    fields: ["eyebrow", "headline", "bullets", "note", "source", "footer"],
    defaults: {
      eyebrow: "What You Get",
      headline: "Everything a producer needs to grow",
      bullets: [
        "Cross-sell revenue modeling",
        "Carrier and product guidance",
        "Marketing assets ready to post",
        "Onboarding and ongoing coaching",
      ],
      note: "",
      footer: "chirpwell.com",
    },
  },
  {
    id: "marketpulse",
    name: "Market Pulse",
    description: "Current state of the industry, cited.",
    tags: ["educational", "stat"],
    fields: ["eyebrow", "headline", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Market Pulse",
      headline: "The market is shifting under producers",
      body: "Clients shop more channels and buy more products. Single-line books feel it first.",
      footer: "chirpwell.com",
    },
  },
  {
    id: "recruit",
    name: "Recruiting",
    description: "Attract producers to your agency.",
    tags: ["recruiting", "promo"],
    fields: ["eyebrow", "headline", "body", "note", "source", "footer"],
    defaults: {
      eyebrow: "Now Contracting",
      headline: "Build a book that pays all twelve months",
      body: "Multi-line producers earn more per client and keep them longer.",
      note: "We hand you the playbook, the products and the pipeline.",
      footer: "chirpwell.com",
    },
  },
  {
    id: "casestudy",
    name: "Client Win",
    description: "A result you delivered, in one card.",
    tags: ["proof", "stat"],
    fields: ["eyebrow", "stat", "statLabel", "note", "source", "footer"],
    defaults: {
      eyebrow: "Broker Win",
      stat: "3x",
      statLabel: "ancillary revenue in twelve months",
      note: "One producer, same client count. We changed the conversation, not the book size.",
      footer: "chirpwell.com",
    },
  },
];

export const EMPTY_CONTENT: TemplateContent = {
  eyebrow: "",
  headline: "",
  body: "",
  stat: "",
  statLabel: "",
  bullets: [],
  footer: "",
  note: "",
  source: "",
  sourceUrl: "",
};


/* ---------------- drawing ---------------- */

const FONT_DISPLAY = "'Playfair Display', Georgia, serif";
const FONT_SANS = "'Inter', 'Helvetica Neue', Arial, sans-serif";

export type FontId =
  | "display"
  | "sans"
  | "georgia"
  | "times"
  | "helvetica"
  | "courier"
  | "impact"
  | "trebuchet";

export const FONT_OPTIONS: { id: FontId; label: string; stack: string }[] = [
  { id: "display", label: "Playfair (serif)", stack: FONT_DISPLAY },
  { id: "sans", label: "Inter (sans)", stack: FONT_SANS },
  { id: "georgia", label: "Georgia", stack: "Georgia, 'Times New Roman', serif" },
  { id: "times", label: "Times", stack: "'Times New Roman', Times, serif" },
  { id: "helvetica", label: "Helvetica", stack: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { id: "trebuchet", label: "Trebuchet", stack: "'Trebuchet MS', Tahoma, sans-serif" },
  { id: "courier", label: "Courier", stack: "'Courier New', Courier, monospace" },
  { id: "impact", label: "Impact", stack: "Impact, 'Arial Black', sans-serif" },
];

/** Content pieces that can each carry their own typeface. */
export type FontSlot =
  | "eyebrow"
  | "stat"
  | "statLabel"
  | "headline"
  | "body"
  | "bullets"
  | "note"
  | "source"
  | "footer";

export const FONT_SLOT_LABELS: Record<FontSlot, string> = {
  eyebrow: "Top label",
  stat: "Headline stat",
  statLabel: "Stat meaning",
  headline: "Main message",
  body: "Supporting detail",
  bullets: "List items",
  note: "Your note",
  source: "Source line",
  footer: "Footer",
};

export const DEFAULT_FONTS: Record<FontSlot, FontId> = {
  eyebrow: "sans",
  stat: "display",
  statLabel: "sans",
  headline: "display",
  body: "sans",
  bullets: "sans",
  note: "sans",
  source: "sans",
  footer: "sans",
};

/** Per-slot size multiplier (1 = template default). */
export const DEFAULT_FONT_SCALE = 1;
export const FONT_SCALE_MIN = 0.6;
export const FONT_SCALE_MAX = 1.6;

/** Decorative shape layers that sit behind the copy. */
export type DecorId =
  | "none"
  | "corner"
  | "orbit"
  | "stripes"
  | "dots"
  | "frame"
  | "arc"
  | "blocks";

export const DECOR_OPTIONS: { id: DecorId; label: string; hint: string }[] = [
  { id: "none", label: "Clean", hint: "No shapes" },
  { id: "corner", label: "Corner wedge", hint: "Angled block" },
  { id: "orbit", label: "Orbit rings", hint: "Concentric circles" },
  { id: "stripes", label: "Diagonal stripes", hint: "Angled lines" },
  { id: "dots", label: "Dot grid", hint: "Even dot field" },
  { id: "frame", label: "Outline frame", hint: "Thin border" },
  { id: "arc", label: "Half circle", hint: "Large arc" },
  { id: "blocks", label: "Stacked bars", hint: "Bar cluster" },
];

export type DecorPlacement = "default" | "flipX" | "flipY" | "flip180";

export const DECOR_PLACEMENTS: { id: DecorPlacement; label: string }[] = [
  { id: "default", label: "Default" },
  { id: "flipX", label: "Mirror \u2194" },
  { id: "flipY", label: "Mirror \u2195" },
  { id: "flip180", label: "Rotate 180\u00B0" },
];

export function fontStack(id: FontId | undefined, fallback: FontId) {
  const found = FONT_OPTIONS.find((f) => f.id === (id ?? fallback));
  return (found ?? FONT_OPTIONS[1]).stack;
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxHeight: number,
  startSize: number,
  weight: string,
  family: string
): { size: number; lines: string[]; lineHeight: number } {
  let size = startSize;
  while (size > 14) {
    ctx.font = `${weight} ${size}px ${family}`;
    const lines = wrapText(ctx, text, maxWidth);
    const lineHeight = size * 1.16;
    if (lines.length * lineHeight <= maxHeight) {
      return { size, lines, lineHeight };
    }
    size -= 4;
  }
  ctx.font = `${weight} ${size}px ${family}`;
  return { size, lines: wrapText(ctx, text, maxWidth), lineHeight: size * 1.16 };
}

function drawGrain(ctx: CanvasRenderingContext2D, w: number, h: number, accent: string) {
  ctx.save();
  ctx.globalAlpha = 0.05;
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1;
  const step = Math.round(w / 24);
  for (let x = step; x < w; x += step) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, h);
    ctx.stroke();
  }
  ctx.restore();
}

function drawDecor(
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  accent: string,
  decor: DecorId,
  opacity: number,
  scale = 1,
  rotation = 0,
  placement: DecorPlacement = "default"
) {
  if (decor === "none") return;
  const m = Math.min(W, H);
  ctx.save();
  ctx.globalAlpha = Math.min(0.85, Math.max(0.03, opacity));
  ctx.fillStyle = accent;
  ctx.strokeStyle = accent;

  // transform around the canvas center so shapes stay inside the frame
  const sx = placement === "flipX" || placement === "flip180" ? -1 : 1;
  const sy = placement === "flipY" || placement === "flip180" ? -1 : 1;
  const k = Math.min(2, Math.max(0.4, scale));
  ctx.translate(W / 2, H / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.scale(sx * k, sy * k);
  ctx.translate(-W / 2, -H / 2);

  if (decor === "corner") {
    ctx.beginPath();
    ctx.moveTo(W, 0);
    ctx.lineTo(W, m * 0.55);
    ctx.lineTo(W - m * 0.55, 0);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(0, H);
    ctx.lineTo(m * 0.28, H);
    ctx.lineTo(0, H - m * 0.28);
    ctx.closePath();
    ctx.fill();
  } else if (decor === "orbit") {
    const cx = W * 0.86;
    const cy = H * 0.18;
    ctx.lineWidth = Math.max(2, m * 0.006);
    for (let i = 1; i <= 5; i++) {
      ctx.beginPath();
      ctx.arc(cx, cy, m * 0.09 * i, 0, Math.PI * 2);
      ctx.stroke();
    }
  } else if (decor === "stripes") {
    ctx.lineWidth = Math.max(3, m * 0.012);
    const gap = m * 0.075;
    const span = (W + H) * 1.5;
    for (let x = -span; x < W + span; x += gap) {
      ctx.beginPath();
      ctx.moveTo(x, H + span);
      ctx.lineTo(x + H + span * 2, -span);
      ctx.stroke();
    }
  } else if (decor === "dots") {
    const gap = m * 0.06;
    const r = Math.max(2, m * 0.006);
    const pad = Math.max(W, H);
    for (let x = -pad; x < W + pad; x += gap) {
      for (let yy = -pad; yy < H + pad; yy += gap) {
        ctx.beginPath();
        ctx.arc(x, yy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else if (decor === "frame") {
    const inset = m * 0.045;
    ctx.lineWidth = Math.max(2, m * 0.005);
    ctx.strokeRect(inset, inset, W - inset * 2, H - inset * 2);
    ctx.lineWidth = Math.max(1, m * 0.002);
    ctx.strokeRect(inset * 1.7, inset * 1.7, W - inset * 3.4, H - inset * 3.4);
  } else if (decor === "arc") {
    ctx.beginPath();
    ctx.arc(W * 0.92, H * 0.88, m * 0.42, Math.PI, Math.PI * 2.5);
    ctx.fill();
  } else if (decor === "blocks") {
    const bw = m * 0.05;
    const baseY = H * 0.78;
    [0.18, 0.32, 0.5, 0.26, 0.4].forEach((hFactor, i) => {
      const bh = m * hFactor;
      ctx.fillRect(W - m * 0.12 - i * bw * 1.5, baseY - bh, bw, bh);
    });
  }
  ctx.restore();
}

export type ImageFit = "cover" | "contain" | "top" | "bottom";

export const IMAGE_FITS: { id: ImageFit; label: string }[] = [
  { id: "cover", label: "Fill (center)" },
  { id: "top", label: "Fill (top)" },
  { id: "bottom", label: "Fill (bottom)" },
  { id: "contain", label: "Fit whole image" },
];

export interface RenderOptions {
  template: TemplateId;
  size: SocialSize;
  palette: Palette;
  content: TemplateContent;
  logo?: HTMLImageElement | null;
  fonts?: Partial<Record<FontSlot, FontId>>;
  fontScales?: Partial<Record<FontSlot, number>>;
  decor?: DecorId;
  decorOpacity?: number;
  decorColor?: string;
  decorScale?: number;
  decorRotation?: number;
  decorPlacement?: DecorPlacement;
  /** Background photo behind the copy. */
  bgImage?: HTMLImageElement | null;
  bgImageFit?: ImageFit;
  /** -1..1 pan of the photo inside the frame. */
  bgOffsetX?: number;
  bgOffsetY?: number;
  /** 1..3 extra zoom applied on top of the fit scale. */
  bgZoom?: number;
  /** 0–1 tint of the palette background laid over the photo. */
  bgScrim?: number;
  /** Auto-pick legible text colors for the current background. */
  autoContrast?: boolean;
  /** Manual override for the text color (hex). */
  textColor?: string;
}

export function renderTemplate(canvas: HTMLCanvasElement, opts: RenderOptions) {
  const {
    size,
    palette: rawPalette,
    content,
    template,
    logo,
    fonts,
    fontScales,
    decor = "none",
    decorOpacity = 0.18,
    decorColor,
    decorScale = 1,
    decorRotation = 0,
    decorPlacement = "default",
    bgImage,
    bgImageFit = "cover",
    bgOffsetX = 0,
    bgOffsetY = 0,
    bgZoom = 1,
    bgScrim = 0.45,
    autoContrast = true,
    textColor,
  } = opts;
  const F = (slot: FontSlot) => fontStack(fonts?.[slot], DEFAULT_FONTS[slot]);
  const SC = (slot: FontSlot) => {
    const v = fontScales?.[slot];
    if (!v || Number.isNaN(v)) return 1;
    return Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, v));
  };
  const SZ = (slot: FontSlot, base: number) => Math.round(base * SC(slot));
  const { width: W, height: H } = size;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const pad = Math.round(Math.min(W, H) * 0.085);
  const contentW = W - pad * 2;

  // background
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, rawPalette.bg);
  grad.addColorStop(1, rawPalette.bgAlt);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const hasPhoto = !!(bgImage && bgImage.complete && bgImage.naturalWidth);
  const scrim = Math.min(1, Math.max(0, bgScrim));
  if (hasPhoto) {
    const iw = bgImage!.naturalWidth;
    const ih = bgImage!.naturalHeight;
    const fitScale =
      bgImageFit === "contain"
        ? Math.min(W / iw, H / ih)
        : Math.max(W / iw, H / ih);
    const zoom = Math.min(4, Math.max(1, bgZoom));
    const dw = iw * fitScale * zoom;
    const dh = ih * fitScale * zoom;
    const clamp1 = (v: number) => Math.min(1, Math.max(-1, v));
    const baseY =
      bgImageFit === "top" ? 0 : bgImageFit === "bottom" ? H - dh : (H - dh) / 2;
    const dx = (W - dw) / 2 + clamp1(bgOffsetX) * (Math.abs(dw - W) / 2);
    const dy = baseY + clamp1(bgOffsetY) * (Math.abs(dh - H) / 2);
    ctx.drawImage(bgImage!, dx, dy, dw, dh);
    // palette tint so copy always has a field to sit on
    if (scrim > 0) {
      const tint = ctx.createLinearGradient(0, 0, W, H);
      tint.addColorStop(0, hexToRgba(rawPalette.bg, scrim));
      tint.addColorStop(1, hexToRgba(rawPalette.bgAlt, Math.min(1, scrim + 0.1)));
      ctx.fillStyle = tint;
      ctx.fillRect(0, 0, W, H);
    }
  }

  // resolve legible colors for whatever is behind the copy
  const paletteLum = (luminance(rawPalette.bg) + luminance(rawPalette.bgAlt)) / 2;
  let bgLum = paletteLum;
  if (hasPhoto) {
    const il = imageLuminance(bgImage!);
    if (il !== null) bgLum = il * (1 - scrim) + paletteLum * scrim;
  }
  const auto = contrastColors(bgLum, rawPalette.accent);
  const resolvedText =
    textColor && isValidHex(textColor)
      ? textColor
      : autoContrast
        ? auto.text
        : rawPalette.text;
  const palette: Palette = {
    ...rawPalette,
    text: resolvedText,
    muted:
      textColor && isValidHex(textColor)
        ? hexToRgba(textColor, 0.66)
        : autoContrast
          ? auto.muted
          : rawPalette.muted,
    accent: autoContrast ? auto.accent : rawPalette.accent,
  };

  if (!hasPhoto) drawGrain(ctx, W, H, palette.accent);
  drawDecor(
    ctx,
    W,
    H,
    decorColor && isValidHex(decorColor) ? decorColor : palette.accent,
    decor,
    decorOpacity,
    decorScale,
    decorRotation,
    decorPlacement
  );

  // accent rule top-left
  ctx.fillStyle = palette.accent;
  ctx.fillRect(pad, pad, Math.round(contentW * 0.14), 6);


  let y = pad + 6 + Math.round(H * 0.05);

  // eyebrow
  if (content.eyebrow) {
    const s = SZ("eyebrow", Math.max(16, Math.round(Math.min(W, H) * 0.024)));
    ctx.font = `600 ${s}px ${F("eyebrow")}`;
    ctx.fillStyle = palette.accent;
    const letters = content.eyebrow.toUpperCase().split("").join("\u2009");
    ctx.fillText(letters, pad, y);
    y += s * 2.1;
  }

  const srcSize = SZ("source", Math.max(13, Math.round(Math.min(W, H) * 0.019)));
  const footerZone =
    pad +
    Math.round(Math.min(W, H) * 0.06) +
    (content.source ? Math.round(srcSize * 2.2) : 0);
  const available = H - y - footerZone;

  const drawParagraph = (
    text: string,
    startSize: number,
    weight: string,
    slot: FontSlot,
    color: string,
    maxH: number
  ) => {
    const family = F(slot);
    const fit = fitFont(
      ctx,
      text,
      contentW,
      maxH * Math.max(1, SC(slot)),
      SZ(slot, startSize),
      weight,
      family
    );
    ctx.fillStyle = color;
    fit.lines.forEach((line, i) => {
      ctx.fillText(line, pad, y + fit.size + i * fit.lineHeight);
    });
    y += fit.size + (fit.lines.length - 1) * fit.lineHeight + fit.size * 0.55;
  };

  // "why we win" note: accent bar + text
  const drawNote = (text: string, maxH: number) => {
    if (!text.trim()) return;
    const startSize = SZ("note", Math.round(Math.min(W, H) * 0.032));
    const barW = Math.max(4, Math.round(Math.min(W, H) * 0.007));
    const inset = barW * 4;
    const fit = fitFont(
      ctx,
      text,
      contentW - inset,
      maxH,
      startSize,
      "500",
      F("note")
    );
    const blockH = fit.size + (fit.lines.length - 1) * fit.lineHeight;
    ctx.fillStyle = palette.accent;
    ctx.fillRect(pad, y, barW, blockH * 1.15);
    ctx.fillStyle = palette.text;
    ctx.font = `500 ${fit.size}px ${F("note")}`;
    fit.lines.forEach((line, i) => {
      ctx.fillText(line, pad + inset, y + fit.size + i * fit.lineHeight);
    });
    y += blockH * 1.15 + fit.size * 0.6;
  };


  if (template === "stat") {
    const statSize = SZ("stat", Math.round(Math.min(W, H) * 0.28));
    ctx.font = `700 ${statSize}px ${F("stat")}`;
    ctx.fillStyle = palette.accent;
    ctx.fillText(content.stat, pad, y + statSize * 0.82);
    y += statSize * 0.98;
    if (content.statLabel) {
      drawParagraph(
        content.statLabel,
        Math.round(Math.min(W, H) * 0.045),
        "600",
        "statLabel",
        palette.text,
        available * 0.3
      );
    }
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.032),
        "400",
        "body",
        palette.muted,
        available * 0.3
      );
    }
  } else if (template === "quote") {
    const markSize = Math.round(Math.min(W, H) * 0.22);
    ctx.font = `700 ${markSize}px ${F("headline")}`;
    ctx.fillStyle = palette.accent;
    ctx.globalAlpha = 0.35;
    ctx.fillText("\u201C", pad, y + markSize * 0.75);
    ctx.globalAlpha = 1;
    y += markSize * 0.55;
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.075),
      "400",
      "headline",
      palette.text,
      available * 0.7
    );
  } else if (template === "checklist") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.062),
      "700",
      "headline",
      palette.text,
      available * 0.34
    );
    const items = content.bullets.filter((b) => b.trim());
    const itemSize = Math.max(
      18,
      Math.min(
        SZ("bullets", Math.round(Math.min(W, H) * 0.038)),
        Math.round((H - y - footerZone) / Math.max(items.length, 1) / 1.9)
      )
    );
    items.forEach((item) => {
      const boxY = y + itemSize * 0.15;
      ctx.strokeStyle = palette.accent;
      ctx.lineWidth = Math.max(2, itemSize * 0.09);
      ctx.strokeRect(pad, boxY, itemSize, itemSize);
      ctx.beginPath();
      ctx.moveTo(pad + itemSize * 0.22, boxY + itemSize * 0.55);
      ctx.lineTo(pad + itemSize * 0.42, boxY + itemSize * 0.76);
      ctx.lineTo(pad + itemSize * 0.8, boxY + itemSize * 0.26);
      ctx.stroke();
      ctx.font = `500 ${itemSize}px ${F("bullets")}`;
      ctx.fillStyle = palette.text;
      const lines = wrapText(ctx, item, contentW - itemSize * 1.8);
      lines.forEach((line, i) => {
        ctx.fillText(line, pad + itemSize * 1.8, boxY + itemSize * 0.85 + i * itemSize * 1.2);
      });
      y = boxY + itemSize * 0.85 + (lines.length - 1) * itemSize * 1.2 + itemSize * 0.95;
    });
  } else if (template === "announcement") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.1),
      "700",
      "headline",
      palette.text,
      available * 0.55
    );
    ctx.fillStyle = palette.accent;
    ctx.fillRect(pad, y - Math.round(Math.min(W, H) * 0.01), Math.round(contentW * 0.3), 4);
    y += Math.round(Math.min(W, H) * 0.04);
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.036),
        "400",
        "body",
        palette.muted,
        available * 0.3
      );
    }
  } else if (template === "question") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.088),
      "700",
      "headline",
      palette.text,
      available * 0.55
    );
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.034),
        "400",
        "body",
        palette.muted,
        available * 0.3
      );
    }
  } else if (template === "cta") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.082),
      "700",
      "headline",
      palette.text,
      available * 0.45
    );
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.034),
        "400",
        "body",
        palette.muted,
        available * 0.25
      );
    }
    const btnH = Math.round(Math.min(W, H) * 0.095);
    const btnFont = Math.round(btnH * 0.34);
    ctx.font = `600 ${btnFont}px ${F("footer")}`;
    const label = (content.footer || "chirpwell.com").toUpperCase();
    const btnW = Math.min(contentW, ctx.measureText(label).width + btnH * 1.4);
    const btnY = Math.min(y + btnH * 0.2, H - footerZone - btnH);
    ctx.fillStyle = palette.accent;
    ctx.beginPath();
    const r = btnH / 2;
    ctx.moveTo(pad + r, btnY);
    ctx.arcTo(pad + btnW, btnY, pad + btnW, btnY + btnH, r);
    ctx.arcTo(pad + btnW, btnY + btnH, pad, btnY + btnH, r);
    ctx.arcTo(pad, btnY + btnH, pad, btnY, r);
    ctx.arcTo(pad, btnY, pad + btnW, btnY, r);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = palette.bg;
    ctx.fillText(label, pad + btnH * 0.7, btnY + btnH * 0.62);
  } else if (template === "proof" || template === "casestudy") {
    const statSize = SZ("stat", Math.round(Math.min(W, H) * 0.22));
    ctx.font = `700 ${statSize}px ${F("stat")}`;
    ctx.fillStyle = palette.accent;
    ctx.fillText(content.stat, pad, y + statSize * 0.82);
    y += statSize * 0.98;
    if (content.statLabel) {
      drawParagraph(
        content.statLabel,
        Math.round(Math.min(W, H) * 0.04),
        "600",
        "statLabel",
        palette.text,
        available * 0.26
      );
    }
    drawNote(content.note, available * 0.34);
  } else if (template === "whywewin") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.082),
      "700",
      "headline",
      palette.text,
      available * 0.5
    );
    drawNote(content.note, available * 0.4);
  } else if (template === "support") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.055),
      "700",
      "headline",
      palette.text,
      available * 0.28
    );
    const items = content.bullets.filter((b) => b.trim());
    const noteH = content.note.trim() ? available * 0.22 : 0;
    const itemSize = Math.max(
      16,
      Math.min(
        SZ("bullets", Math.round(Math.min(W, H) * 0.034)),
        Math.round(
          (H - y - footerZone - noteH) / Math.max(items.length, 1) / 2
        )
      )
    );
    items.forEach((item) => {
      const dotY = y + itemSize * 0.55;
      ctx.fillStyle = palette.accent;
      ctx.beginPath();
      ctx.arc(pad + itemSize * 0.3, dotY, itemSize * 0.18, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = `500 ${itemSize}px ${F("bullets")}`;
      ctx.fillStyle = palette.text;
      const lines = wrapText(ctx, item, contentW - itemSize * 1.6);
      lines.forEach((line, i) => {
        ctx.fillText(
          line,
          pad + itemSize * 1.2,
          y + itemSize * 0.85 + i * itemSize * 1.2
        );
      });
      y += itemSize * 0.85 + (lines.length - 1) * itemSize * 1.2 + itemSize * 0.9;
    });
    y += itemSize * 0.3;
    drawNote(content.note, noteH || available * 0.2);
  } else if (template === "marketpulse") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.07),
      "700",
      "headline",
      palette.text,
      available * 0.45
    );
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.034),
        "400",
        "body",
        palette.muted,
        available * 0.35
      );
    }
  } else if (template === "recruit") {
    drawParagraph(
      content.headline,
      Math.round(Math.min(W, H) * 0.072),
      "700",
      "headline",
      palette.text,
      available * 0.38
    );
    if (content.body) {
      drawParagraph(
        content.body,
        Math.round(Math.min(W, H) * 0.032),
        "400",
        "body",
        palette.muted,
        available * 0.24
      );
    }
    drawNote(content.note, available * 0.26);
  }

  // source citation
  if (content.source) {
    ctx.font = `500 ${srcSize}px ${F("source")}`;
    ctx.fillStyle = palette.muted;
    const srcText = content.sourceUrl
      ? `Source: ${content.source} \u2022 ${content.sourceUrl}`
      : `Source: ${content.source}`;
    const srcLines = wrapText(ctx, srcText, contentW).slice(0, 2);
    srcLines.forEach((line, i) => {
      ctx.fillText(
        line,
        pad,
        H - pad * 0.7 - srcSize * (2.0 + (srcLines.length - 1 - i) * 1.25)
      );
    });
  }

  // footer
  const fSize = SZ("footer", Math.max(14, Math.round(Math.min(W, H) * 0.022)));
  ctx.font = `500 ${fSize}px ${F("footer")}`;
  ctx.fillStyle = palette.muted;
  const footerText = template === "cta" ? "Chirpwell" : content.footer;
  if (footerText) {
    ctx.fillText(footerText, pad, H - pad * 0.7);
  }

  if (logo && logo.complete && logo.naturalWidth) {
    const lh = Math.round(Math.min(W, H) * 0.045);
    const lw = (logo.naturalWidth / logo.naturalHeight) * lh;
    ctx.drawImage(logo, W - pad - lw, H - pad * 0.7 - lh * 0.78, lw, lh);
  }
}
